import { AfterContentInit, AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import moment from 'moment';
import { fadeIn, fadeOut, slideDown, slideUp } from 'src/app/animations';
import { AuthPage } from 'src/app/models/auth/auth-page.model';
import { SiteNote } from 'src/app/models/site-notes/site-note.model';
import { UserAccountInfo } from 'src/app/models/user-account-info.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { SiteNotesService } from 'src/app/services/sections/site-notes/site-notes.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ButtonIcons } from '../action-button/models/button-icons.enum';
import { LoginRedirectPage } from '../login-wizard/models/login-redirect-page.enum';
import { AuthService } from 'src/app/services/auth.service';
import { Location } from '@angular/common';
export class SiteNotesFilter {
	assignedToMe: boolean;
	notCompleted: boolean;
	numberOfDays?: number
	searchQuery: string;

	static get default(): SiteNotesFilter {
		return <SiteNotesFilter>{
			assignedToMe: false,
			notCompleted: true,
			numberOfDays: null,
			searchQuery: ''
		};
	}
}

export interface SiteNotesFilterCache {
	jobId?: number;
	filter: SiteNotesFilter
}

@Component({
	selector: 'site-notes',
	templateUrl: './site-notes.component.html',
	styleUrls: ['./site-notes.component.scss', '../stylesheets/job-actions.scss', '../stylesheets/job-main.scss', '../stylesheets/job-inputs.scss'],
	animations: [slideUp(100), slideDown(100), fadeIn(200), fadeOut(200)]
})
export class SiteNotesComponent implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {

	public self = this;

	public isFilterApplied = false;

	public deficienciesUrl: string;
	public myWorkUrl: string;
	public allowSiteNotes: boolean;

	public siteNotes: SiteNote[] = [];
	public backupSiteNotes: SiteNote[] = [];
	public sites: { name: string, siteId: number }[] = [];
	public categories: { name: string, categoryId: number }[] = [];
	public notePossibleAssignees: { name: string, assigneeId: number }[] = [];

	public userAccountInfoModel: UserAccountInfo;
	public accountRouteUrls: Map<AuthPage, string>;

	public jobId: number | null;

	public siteNoteFormGroup: FormGroup;
	public filtersFormModel: FormGroup;
	public backupFilters: any;

	public readonly ButtonIcons = ButtonIcons;
	public readonly moment = moment;

	private siteNotesFilterKey: string = 'site-notes-filter';
	private siteNotesJobIdKey: string = 'site-notes-jobId';
	public canCreateSiteNote: boolean = false;
	
	constructor(
		private route: ActivatedRoute,
		private authService: AuthService,
		private router: Router,
		private siteNotesService: SiteNotesService,
		private jwt: JwtHelperService,
		private location: Location
	) {
		this.siteNotes = this.route.snapshot.data.siteNotes as SiteNote[];
		this.userAccountInfoModel = this.route.snapshot.data.accountInfo as UserAccountInfo;
		this.allowSiteNotes = true;// !this.userAccountInfoModel.isVendorUser || (this.userAccountInfoModel.isVendorUser && this.userAccountInfoModel.isSelfPerform);
		this.accountRouteUrls = this.route.snapshot.data.accountRouteUrls;
		this.jobId = +this.route.snapshot.params['jobId'];
	}

	ngOnInit(): void {
		GlobalSettingsService.isJobCompletedStatusVisible = false;
		const defaultFilter: SiteNotesFilter = this.getFilter();
		this.filtersFormModel = new FormGroup({
			assignedToMe: new FormControl(defaultFilter.assignedToMe),
			notCompleted: new FormControl(defaultFilter.notCompleted),
			numberOfDays: new FormControl(defaultFilter.numberOfDays),
			searchQuery: new FormControl(defaultFilter.searchQuery)
		});
				
		this.siteNotesService.canCreateSiteNotes(TokenHelper.getToken(this.route), this.jobId).subscribe({
			next: (data: boolean) => {
				this.canCreateSiteNote = data;
			}
		});
		
		this.injectActionButtons();

		this.filtersFormModel.get('numberOfDays').valueChanges
			.subscribe(value => {
				if (!value) {
					this.filtersFormModel.get('numberOfDays').setValue(null, { emitEvent: false });
				} else if (isNaN(parseInt(value, 10))) {
					this.filtersFormModel.get('numberOfDays').setValue(null, { emitEvent: false });
				} else {
					if (value < 0) { this.filtersFormModel.get('numberOfDays').setValue(null, { emitEvent: false }); }
					else if (value > 9999) { this.filtersFormModel.get('numberOfDays').setValue(9999, { emitEvent: false }); }
					else { this.filtersFormModel.get('numberOfDays').setValue(parseInt(value, 10), { emitEvent: false }); }
				}
			});
		
		this.applyFilters();
	}

	private setFilterCache(filterData: SiteNotesFilter): void {
		sessionStorage.setItem(this.siteNotesFilterKey, JSON.stringify(<SiteNotesFilterCache> {
			jobId: this.jobId, 
			filter: filterData
		}));
	}

	public getFilter(removeFilterCache: boolean = true): SiteNotesFilter {
		let filter: SiteNotesFilter = SiteNotesFilter.default;
		const cacheFilterStr: string = sessionStorage.getItem(this.siteNotesFilterKey);
		if(!!removeFilterCache) {
			sessionStorage.removeItem(this.siteNotesFilterKey);
		}		
		
		if(!!cacheFilterStr) {
			const cacheFilter: SiteNotesFilterCache = JSON.parse(cacheFilterStr) as SiteNotesFilterCache;
			if(!!cacheFilter.jobId) {
				if(!!this.jobId && cacheFilter.jobId === this.jobId) {
					filter = {...cacheFilter.filter};
				}
			}
			else {
				if(!this.jobId) {
					filter = {...cacheFilter.filter};
				}
			}			
		}	
		
		return filter;
	}
	
	ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, 0);
	}

	ngAfterContentInit(): void {
		let jobIdStr = sessionStorage.getItem(this.siteNotesJobIdKey);
		sessionStorage.removeItem(this.siteNotesJobIdKey);
		let jobId = !!jobIdStr ? +jobIdStr : null;

		if (!!jobId && !location.href.endsWith(jobId?.toString())) {			
			setTimeout(() => {
				this.router.navigate([`./${jobId}`], { relativeTo: this.route, replaceUrl: true});
			}, 200);			
		}
	}

	ngOnDestroy(): void {
		GlobalSettingsService.navigationBarActions.delete('site_notes_close_page');
	}

	public deleteSiteNote(siteNoteId: number): void {
		this.siteNotesService.deleteSiteNote(TokenHelper.getToken(this.route), siteNoteId)
			.subscribe();
	}

	public onHide(): void {
		if (this.filtersFormModel) {
			this.filtersFormModel.patchValue(this.backupFilters);
		}

		this.isFilterApplied = this.isFilterChanged(this.backupFilters);
	}

	private isFilterChanged(filter: SiteNotesFilter): boolean {
		return JSON.stringify(filter) != JSON.stringify(SiteNotesFilter.default);
	}
	
	public applyFilters(): void {
		const filterData: SiteNotesFilter = this.filtersFormModel.getRawValue() as SiteNotesFilter; 
		const query = filterData.searchQuery.toLowerCase();

		this.backupFilters = filterData;

		this.siteNotesService.getSiteNotesList(TokenHelper.getToken(this.route), {...filterData, jobId: this.jobId})
			.subscribe(result => {
				this.backupSiteNotes = result || [];
				this.siteNotes = this.backupSiteNotes.filter(item =>
					!filterData.searchQuery ||
					item.siteName?.toLowerCase().includes(query) ||
					item.siteAddress?.toLowerCase().includes(query) ||
					item.category?.toLowerCase().includes(query) ||
					item.message?.toLowerCase().includes(query) ||
					item.completionNote?.toLowerCase().includes(query));
			});

		this.setFilterCache(filterData)		
		this.isFilterApplied = this.isFilterChanged(filterData);
	}
	
	public redirectToSiteNote(siteNote: SiteNote): void {
		sessionStorage.setItem(this.siteNotesJobIdKey, this.jobId.toString());
		this.router.navigate([siteNote.siteNoteId], { relativeTo: this.route, state: { 'userAccountInfoModel': this.userAccountInfoModel } });
	}

	private injectActionButtons(): void {
		GlobalSettingsService.navigationBarActions.set(
			'site_notes_close_page',
			{
				icon: 'close',
				listOrder: 1,
				isInDropdownMenu: false,
				isTextOnlyShownInDropdown: true,
				action: () => {
					let backUrl = sessionStorage.getItem('digital-log-back-url');
					sessionStorage.removeItem('digital-log-back-url');
					if (backUrl){
						window.open(backUrl,"_self");
					} else {
						this.router.navigate(['/', ...this.accountRouteUrls.get(AuthPage.MyWork).split('/')]);
					}
				}
			}
		);
	}

	onAddPressed(): void {
		sessionStorage.setItem(this.siteNotesJobIdKey, this.jobId.toString());
		this.router.navigate(['add'], { relativeTo: this.route });
	}


}
